// The bottom-anchored "map section" — a fixed-position pane that houses
// the maplibre canvas, the drag handle used to expand it, the selected
// tag chips overlay, and the venue / experience preview sheets. Extracted
// from `GuestMapPage` so the page-level render stays a clean composition.
//
// The pane is hidden without removing its dimensions while the keyboard is
// open — Android's fixed-position anchoring otherwise pushes the mini-map
// above the keyboard and covers the search suggestions.

import React, { useMemo } from 'react';
import { distanceKmFromUser as distanceKmFromUserUtil } from './distance';
import {
  formatVenueDistance,
  MapVenuePreviewSheet,
} from './GuestMapSurface';
import { ExperiencePreviewSheet } from './ExperiencePreviewSheet';
import { MapDragHandle } from './MapDragHandle';
import { MapSelectedTagsOverlay } from './MapSelectedTagsOverlay';
import { MapCenterOnMeButton } from './MapCenterOnMeButton';
import { MapMinimizeButton } from './MapMinimizeButton';
import { MAP_ANIM_MS, NEARBY_RADIUS_KM } from './guestMapConstants';

export function MapFixedSection({
  // Layout / lifecycle
  keyboardOpen,
  totalSectionHeight,
  isDragging,
  isExpanded,
  mapCanvasRef,
  mapTheme,
  effectiveNavOffset,
  // Drag / interaction
  onPointerDown,
  onPointerMove,
  onPointerUp,
  openMapFully,
  minimizeMap,
  centerOnMe,
  // Tags
  selectedVenueTags,
  selectedExperienceTags,
  setSelectedVenueTags,
  setSelectedExperienceTags,
  // Preview data
  allVenues,
  experiences,
  selectedMapVenueId,
  selectedExperienceId,
  userPosition,
  defaultCenter,
  eventsByVenueId,
  // Preview handlers
  openVenueChat,
  hasActivePass,
  setSelectedMapVenueId,
  setSelectedExperienceId,
  setPinnedVenueId,
  setExpandedVenueId,
  setExperiences,
  venueListRef,
}) {
  const selectedExperience = useMemo(
    () => experiences.find((e) => e.id === selectedExperienceId) || null,
    [experiences, selectedExperienceId],
  );
  const selectedMapVenue = useMemo(
    () => allVenues.find((v) => v.id === selectedMapVenueId) || null,
    [allVenues, selectedMapVenueId],
  );
  const selectedMapVenueIndex = useMemo(
    () => allVenues.findIndex((v) => v.id === selectedMapVenueId),
    [allVenues, selectedMapVenueId],
  );
  const selectedDistanceText = useMemo(
    () => (selectedMapVenue ? formatVenueDistance(userPosition || defaultCenter, selectedMapVenue) : ''),
    [defaultCenter, selectedMapVenue, userPosition],
  );
  const selectedIsFarAway = useMemo(() => {
    if (!selectedMapVenue || !userPosition) return false;
    return distanceKmFromUserUtil(selectedMapVenue, userPosition) > NEARBY_RADIUS_KM;
  }, [selectedMapVenue, userPosition]);

  return (
    <div
      data-testid="map-section"
      data-expanded={isExpanded}
      aria-hidden={keyboardOpen}
      style={{
        position: 'fixed',
        // Keep canvas dimensions measurable without covering suggestions.
        bottom: keyboardOpen ? '-140vh' : 0,
        // NO CSS `transform` here. An ancestor transform on the map's
        // parent creates a nested compositor coordinate system; on
        // Android WebView, MapLibre marker transforms (translate3d per
        // frame) get subpixel-misaligned against the GPU-scaled tile
        // canvas during continuous pinch-zoom, which read as "all pins
        // drift as a group" — the exact user-reported symptom. We
        // centre horizontally with `left: 0; right: 0; margin: 0 auto`
        // instead, which places the fixed element purely via layout
        // (no transform, no new containing block for descendants).
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        maxWidth: 390,
        height: totalSectionHeight,
        zIndex: 50,
        transition: isDragging ? 'none' : `height ${MAP_ANIM_MS}ms cubic-bezier(.22,1,.36,1)`,
        pointerEvents: keyboardOpen ? 'none' : 'auto',
        visibility: keyboardOpen ? 'hidden' : 'visible',
        // Rounded clipping is applied EXACTLY ONCE on this outer section
        // (via borderRadius + overflow:hidden). The nested rounded clips
        // and the `clip-path: inset(... round 30px)` that used to wrap
        // the WebGL canvas were removed: on Android WebView, a mask
        // layer over a WebGL canvas is composited separately from the
        // DOM marker layer, which caused all markers to appear to
        // "slide as a group" against the tiles during pinch-zoom.
        overflow: 'hidden',
        borderRadius: 30,
      }}
    >
      <div style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: 'var(--surface-2)',
        border: 'none',
        boxShadow: 'var(--premium-shadow-nested)',
      }}>
        {!isExpanded ? (
          <MapDragHandle
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          />
        ) : null}

        <div ref={mapCanvasRef} data-testid="guest-map-canvas" style={{
          width: '100%',
          height: '100%',
          background: mapTheme === 'dark' ? '#273252' : '#EAF0FF',
        }} />

        {isExpanded ? (
          <MapSelectedTagsOverlay
            selectedVenueTags={selectedVenueTags}
            selectedExperienceTags={selectedExperienceTags}
            onRemoveVenueTag={(tag) => setSelectedVenueTags((prev) => prev.filter((t) => t !== tag))}
            onRemoveExperienceTag={(tag) => setSelectedExperienceTags((prev) => prev.filter((t) => t !== tag))}
          />
        ) : null}

        {!isExpanded ? (
          <button
            type="button"
            data-testid="map-open-overlay"
            onClick={openMapFully}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))',
              border: 'none',
              cursor: 'pointer',
              zIndex: 20,
              touchAction: 'none',
            }}
          />
        ) : null}

        {isExpanded && selectedMapVenue ? (
          <MapVenuePreviewSheet
            venue={selectedMapVenue}
            idx={Math.max(selectedMapVenueIndex, 0)}
            distanceText={selectedDistanceText}
            isFarAway={selectedIsFarAway}
            theme={mapTheme}
            venueEvents={eventsByVenueId.get(selectedMapVenue.id) || []}
            onClose={() => setSelectedMapVenueId(null)}
            onMessage={openVenueChat}
            hasActivePass={hasActivePass}
            onCardTap={(v) => {
              setSelectedMapVenueId(null);
              setPinnedVenueId(v.id);
              setExpandedVenueId(v.id);
              minimizeMap();
              setTimeout(() => {
                venueListRef.current?.scrollToVenue(v.id);
              }, 360);
            }}
          />
        ) : null}

        {isExpanded && selectedExperience ? (
          <ExperiencePreviewSheet
            experience={selectedExperience}
            onClose={() => setSelectedExperienceId('')}
            onReactionChange={(id, patch) => {
              setExperiences((prev) => prev.map((e) => (
                e.id === id ? { ...e, ...patch } : e
              )));
            }}
          />
        ) : null}
      </div>

      {isExpanded ? (
        <MapCenterOnMeButton onClick={centerOnMe} navOffset={effectiveNavOffset} />
      ) : null}
      {isExpanded ? <MapMinimizeButton onClick={minimizeMap} /> : null}
    </div>
  );
}
